﻿import { Button } from '@progress/kendo-react-buttons';
import { Window } from '@progress/kendo-react-dialogs';
import { NumericTextBox, TextBox } from '@progress/kendo-react-inputs';
import React from 'react';
import ReactDOM from 'react-dom';
import { DEFAULT_WELCOME_HELPER, WelcomeHelperSettings } from '../../types/settings';
import { useAppDispatch, useAppSelector } from '../hooks/store-hooks';
import { setSetting } from '../state/slices/settings-slice';
import PulseHighlight from './inputs/PulseHighlight';

type OverlayPosition =
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'center-left'
    | 'center'
    | 'center-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';

const POSITION_GRID: { pos: OverlayPosition; symbol: string }[][] = [
    [
        { pos: 'top-left', symbol: '↖' },
        { pos: 'top-center', symbol: '↑' },
        { pos: 'top-right', symbol: '↗' }
    ],
    [
        { pos: 'center-left', symbol: '←' },
        { pos: 'center', symbol: '●' },
        { pos: 'center-right', symbol: '→' }
    ],
    [
        { pos: 'bottom-left', symbol: '↙' },
        { pos: 'bottom-center', symbol: '↓' },
        { pos: 'bottom-right', symbol: '↘' }
    ]
];

const POSITION_COORDS: Record<OverlayPosition, { left: number; top: number }> = {
    'top-left': { left: 25, top: 25 },
    'top-center': { left: 50, top: 25 },
    'top-right': { left: 75, top: 25 },
    'center-left': { left: 25, top: 50 },
    center: { left: 50, top: 50 },
    'center-right': { left: 75, top: 50 },
    'bottom-left': { left: 25, top: 75 },
    'bottom-center': { left: 50, top: 75 },
    'bottom-right': { left: 75, top: 75 }
};

interface WelcomeHelperProps {
    showWindow?: boolean;
    onClose?: () => void;
}

export default function WelcomeHelper({ showWindow, onClose }: WelcomeHelperProps) {
    const dispatch = useAppDispatch();
    const settings = useAppSelector((x) => x.settings.welcomeHelper);
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);

    const [overlayContainer, setOverlayContainer] = React.useState<HTMLElement | null>(() =>
        document.getElementById('workspace-maps-overlay')
    );

    React.useEffect(() => {
        const sync = () => {
            const el = document.getElementById('workspace-maps-overlay');
            setOverlayContainer((prev) => (prev === el ? prev : el));
        };
        const root = document.getElementById('app-router-content') ?? document.body;
        const observer = new MutationObserver(sync);
        observer.observe(root, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    const update = (patch: Partial<WelcomeHelperSettings>) => {
        dispatch(setSetting('welcomeHelper', { ...settings, ...patch }));
    };

    const handleSvgFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const raw = (ev.target?.result as string) ?? '';
            try {
                const doc = new DOMParser().parseFromString(raw, 'image/svg+xml');
                const svgEl = doc.documentElement;
                svgEl.removeAttribute('width');
                svgEl.removeAttribute('height');
                svgEl.style.width = '180px';
                svgEl.style.height = 'auto';
                svgEl.style.display = 'block';
                update({ svgContent: new XMLSerializer().serializeToString(svgEl) });
            } catch {
                update({ svgContent: raw });
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const clearSvg = () => {
        update({ svgContent: null });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const position = (settings.position ?? 'bottom-right') as OverlayPosition;
    const base = POSITION_COORDS[position] ?? POSITION_COORDS['bottom-right'];
    const left = `${base.left + (settings.offsetX ?? 0)}%`;
    const top = `${base.top + (settings.offsetY ?? 0)}%`;

    const overlayPortal =
        overlayContainer && settings.visible
            ? ReactDOM.createPortal(
                  <div
                      style={{
                          position: 'absolute',
                          left,
                          top,
                          transform: 'translate(-50%, -50%)',
                          transformOrigin: 'center center',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: `${settings.gap ?? 8}px`,
                          opacity: settings.opacity,
                          pointerEvents: 'none'
                      }}>
                      {settings.text && (
                          <div
                              style={{
                                  color: settings.textColor ?? '#ffffff',
                                  fontSize: `${settings.fontSize ?? 1.6}rem`,
                                  fontWeight: settings.fontWeight ?? 'bold',
                                  fontFamily: "'Red Hat Display', sans-serif",
                                  textShadow: '0 1px 4px rgba(0,0,0,0.8)',
                                  letterSpacing: '0.05em',
                                  whiteSpace: 'nowrap'
                              }}>
                              {settings.text}
                          </div>
                      )}
                      {settings.svgContent && (
                          <div
                              style={{ transform: `scale(${settings.scale ?? 1})`, transformOrigin: 'center center' }}
                              dangerouslySetInnerHTML={{ __html: settings.svgContent }}
                          />
                      )}
                  </div>,
                  overlayContainer
              )
            : null;

    const exportJson = () => {
        const json = JSON.stringify(settings, null, 2);
        if (navigator.clipboard) {
            navigator.clipboard
                .writeText(json)
                .then(() => {
                    alert('Settings copied to clipboard!');
                })
                .catch(() => {
                    prompt('Copy settings JSON (Ctrl+A, Ctrl+C):', json);
                });
        } else {
            prompt('Copy settings JSON (Ctrl+A, Ctrl+C):', json);
        }
    };

    const importJson = () => {
        const raw = prompt('Paste settings JSON:');
        if (!raw) {
            return;
        }
        try {
            const parsed = JSON.parse(raw) as Partial<WelcomeHelperSettings>;
            update(parsed);
        } catch {
            alert('Invalid JSON');
        }
    };

    return (
        <>
            {overlayPortal}
            {showWindow && (
                <Window title="Welcome overlay helper" draggable onClose={onClose} initialWidth={430} initialHeight={600}>
                    <div className="d-flex flex-column gap-3 p-2">
                        <div>
                            <div className="k-font-size-sm k-mb-1 secondary-text-color">Text</div>
                            <TextBox value={settings.text} onChange={(e) => update({ text: (e.value as string) ?? '' })} />
                        </div>

                        <div>
                            <div className="k-font-size-sm k-mb-1 secondary-text-color">Text size (rem)</div>
                            <NumericTextBox
                                value={settings.fontSize ?? 1.6}
                                min={0.5}
                                max={20}
                                step={0.1}
                                format="0.##"
                                onChange={(e) => update({ fontSize: e.value ?? 1.6 })}
                            />
                        </div>

                        <div>
                            <div className="k-font-size-sm k-mb-1 secondary-text-color">Text color</div>
                            <input
                                type="color"
                                value={settings.textColor ?? '#ffffff'}
                                onChange={(e) => update({ textColor: e.target.value })}
                                style={{ width: '100%', height: 32, cursor: 'pointer', border: 'none', padding: 0 }}
                            />
                        </div>

                        <div>
                            <div className="k-font-size-sm k-mb-1 secondary-text-color">Font weight</div>
                            <div className="d-flex gap-2">
                                {(['normal', 'bold'] as const).map((w) => (
                                    <Button
                                        key={w}
                                        size="small"
                                        fillMode="solid"
                                        themeColor={(settings.fontWeight ?? 'bold') === w ? 'info' : 'base'}
                                        onClick={() => update({ fontWeight: w })}>
                                        {w.charAt(0).toUpperCase() + w.slice(1)}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="k-font-size-sm k-mb-1 secondary-text-color">SVG scale</div>
                            <NumericTextBox
                                value={settings.scale}
                                min={0.1}
                                max={10}
                                step={0.1}
                                format="0.##"
                                onChange={(e) => update({ scale: e.value ?? 1 })}
                            />
                        </div>

                        <div>
                            <div className="k-font-size-sm k-mb-1 secondary-text-color">Gap between text and SVG (px)</div>
                            <NumericTextBox
                                value={settings.gap ?? 8}
                                min={0}
                                max={100}
                                step={1}
                                format="0"
                                onChange={(e) => update({ gap: e.value ?? 8 })}
                            />
                        </div>

                        <div>
                            <div className="k-font-size-sm k-mb-1 secondary-text-color">Opacity</div>
                            <NumericTextBox
                                value={settings.opacity}
                                min={0}
                                max={1}
                                step={0.05}
                                format="0.##"
                                onChange={(e) => update({ opacity: e.value ?? 0.85 })}
                            />
                        </div>

                        <div>
                            <div className="k-font-size-sm k-mb-1 secondary-text-color">SVG File</div>
                            <div className="d-flex gap-2 align-items-center">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".svg,image/svg+xml"
                                    onChange={handleSvgFile}
                                    style={{ flex: 1, minWidth: 0, fontSize: '0.8em' }}
                                />
                                {settings.svgContent && (
                                    <Button size="small" fillMode="outline" onClick={clearSvg}>
                                        Clear
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div>
                            <div className="k-font-size-sm k-mb-1 secondary-text-color">Map position</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 36px)', gap: 4 }}>
                                {POSITION_GRID.flat().map(({ pos, symbol }) => (
                                    <Button
                                        key={pos}
                                        size="small"
                                        fillMode="solid"
                                        themeColor={pos === position ? 'info' : 'base'}
                                        onClick={() => update({ position: pos, offsetX: 0, offsetY: 0 })}
                                        title={pos}>
                                        {symbol}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="k-font-size-sm k-mb-1 secondary-text-color">Position offset (%)</div>
                            <div className="d-flex gap-2 align-items-center">
                                <div style={{ flex: 1 }}>
                                    <div className="k-font-size-xs secondary-text-color k-mb-1">X</div>
                                    <NumericTextBox
                                        value={settings.offsetX ?? 0}
                                        min={-50}
                                        max={50}
                                        step={0.5}
                                        format="0.#"
                                        onChange={(e) => update({ offsetX: e.value ?? 0 })}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div className="k-font-size-xs secondary-text-color k-mb-1">Y</div>
                                    <NumericTextBox
                                        value={settings.offsetY ?? 0}
                                        min={-50}
                                        max={50}
                                        step={0.5}
                                        format="0.#"
                                        onChange={(e) => update({ offsetY: e.value ?? 0 })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="d-flex gap-2 flex-wrap">
                            <PulseHighlight active={!settings.visible}>
                                <Button
                                    fillMode="solid"
                                    themeColor={settings.visible ? 'secondary' : 'success'}
                                    onClick={() => update({ visible: !settings.visible })}>
                                    {settings.visible ? 'Hide overlay' : 'Show overlay'}
                                </Button>
                            </PulseHighlight>
                            <Button fillMode="outline" onClick={() => dispatch(setSetting('welcomeHelper', DEFAULT_WELCOME_HELPER))}>
                                Reset
                            </Button>
                            <Button fillMode="outline" onClick={exportJson} title="Copy settings JSON to clipboard">
                                Export
                            </Button>
                            <Button fillMode="outline" onClick={importJson} title="Paste settings JSON">
                                Import
                            </Button>
                        </div>
                    </div>
                </Window>
            )}
        </>
    );
}
